import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { InvitationStatus, Shop, ShopInvitation } from '@reims-local/types';
import { adminApi } from '../lib/api';

export function Invitations() {
  const [rows, setRows] = useState<Array<{ invitation: ShopInvitation; shop: Shop }>>([]);
  const [statusFilter, setStatusFilter] = useState<InvitationStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    adminApi
      .listInvitations(statusFilter || undefined)
      .then((res) => {
        setRows(res.data);
        setError(null);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const kpis = {
    sent: rows.filter((r) => r.invitation.status === 'sent').length,
    opened: rows.filter((r) => r.invitation.status === 'opened').length,
    activated: rows.filter((r) => r.invitation.status === 'activated').length,
    expired: rows.filter((r) => r.invitation.status === 'expired').length,
  };

  return (
    <>
      <h2>Invitations</h2>

      <div className="grid-kpi">
        <Kpi label="Envoyées" value={kpis.sent} />
        <Kpi label="Ouvertes" value={kpis.opened} />
        <Kpi label="Activées" value={kpis.activated} />
        <Kpi label="Expirées" value={kpis.expired} />
      </div>

      <div className="toolbar">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as InvitationStatus | '')}>
          <option value="">Tous statuts</option>
          <option value="sent">Envoyée</option>
          <option value="opened">Ouverte</option>
          <option value="activated">Activée</option>
          <option value="expired">Expirée</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p>Chargement…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Boutique</th>
              <th>Email</th>
              <th>Statut</th>
              <th>Envoyée</th>
              <th>Ouverte</th>
              <th>Activée</th>
              <th>Expire</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ invitation, shop }) => (
              <tr key={invitation.id}>
                <td>
                  <Link to={`/shops/${shop.id}`}>{shop.name}</Link>
                </td>
                <td>{invitation.email}</td>
                <td>
                  <span className={`badge ${badgeClass(invitation.status)}`}>{invitation.status}</span>
                </td>
                <td className="muted">{fmt(invitation.created_at)}</td>
                <td className="muted">{invitation.opened_at ? fmt(invitation.opened_at) : '—'}</td>
                <td className="muted">{invitation.activated_at ? fmt(invitation.activated_at) : '—'}</td>
                <td className="muted">{fmt(invitation.expires_at)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                  Aucune invitation.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

function badgeClass(s: InvitationStatus): string {
  if (s === 'activated') return 'green';
  if (s === 'opened') return 'blue';
  if (s === 'expired') return 'red';
  return 'orange';
}

function fmt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
